import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { ArrowLeft, RefreshCw } from "lucide-react";

export default function LegalUpdates() {
  const navigate = useNavigate();
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get("/legal-updates")
      .then((r) => setUpdates(r.data.updates || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <header className="flex items-center justify-between px-6 md:px-12 py-6 border-b border-border">
        <Brand />
        <Button variant="ghost" onClick={() => navigate(-1)} data-testid="legal-updates-back-btn"
                className="rounded-none text-muted-foreground hover:text-primary">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-14">
        <div className="inline-flex items-center gap-2 border border-primary/30 bg-primary/5 px-3 py-1.5 mb-6">
          <RefreshCw className="w-3.5 h-3.5 text-primary" />
          <span className="overline text-primary">Automatic Legal &amp; Tax Law Monitoring</span>
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl font-semibold tracking-tight mb-3">
          Recent Law Changes
        </h1>
        <p className="text-sm text-muted-foreground leading-relaxed mb-10">
          Leego AI checks the official federal sources it's grounded in — the eCFR (29 C.F.R. Part 18) and the
          U.S. Code (26 U.S.C., individual income tax) — every 24 hours. When the actual text of a section
          changes, it shows up here, dated, with a link to the official source and what the text used to say.
        </p>
        {loading && <p className="text-sm text-muted-foreground">Checking for updates…</p>}
        {!loading && error && (
          <p className="text-sm text-muted-foreground">
            Couldn't load recent updates right now. Try again shortly.
          </p>
        )}
        {!loading && !error && updates.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No changes detected yet in the monitored sources. Leego's automatic daily check hasn't found any
            differences from the last time it read the official text.
          </p>
        )}
        <div className="space-y-8">
          {updates.map((u, i) => (
            <section key={i} data-testid={`legal-update-${i}`} className="border-l-2 border-primary/30 pl-4">
              <div className="font-mono-tech text-xs text-muted-foreground mb-1">
                {u.detected_at ? new Date(u.detected_at).toLocaleDateString(undefined, {
                  year: "numeric", month: "long", day: "numeric",
                }) : ""} · {u.source}
              </div>
              <h2 className="font-serif-display text-lg font-semibold text-foreground mb-2">
                {u.citation} — {u.title}
              </h2>
              {u.old_text && u.new_text && (
                <div className="grid gap-3 sm:grid-cols-2 text-xs text-muted-foreground">
                  <div>
                    <div className="uppercase tracking-wide mb-1 text-[10px]">Previously</div>
                    <p className="leading-relaxed line-through decoration-muted-foreground/40">{u.old_text}</p>
                  </div>
                  <div>
                    <div className="uppercase tracking-wide mb-1 text-[10px] text-primary">Now</div>
                    <p className="leading-relaxed text-foreground">{u.new_text}</p>
                  </div>
                </div>
              )}
              {u.source_url && (
                <a href={u.source_url} target="_blank" rel="noopener noreferrer"
                   className="inline-block mt-2 text-xs text-primary hover:underline">
                  View official source
                </a>
              )}
            </section>
          ))}
        </div>
      </main>
    </div>
  );
}
